import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArtButton,
  ArtDialog,
  ArtTable,
  ArtTableHandle,
  confirmDialog,
  FormFieldConsolidatedProps,
  HeadCell,
  SSRTableBuilder,
  useApiService,
} from "@art/shared-ui";
import { usePageWrapper } from "../../../hooks";
import { AttributePermissionGate, useAttributePermission } from "../../permissions";
import { useNavigate, useSearchParams } from "react-router-dom";
import { faPlus, faTrash, faEdit } from "@fortawesome/free-solid-svg-icons";
import { Box, Chip, LinearProgress, Tooltip } from "@mui/material";
import ArtUserAddPage from "./add-art-user.component";
import ArtRoleAssignDialog from "./assign-role";
import { tableParamNav } from "../../../utils/table-utils";

const ArtConnectionList = usePageWrapper(
  function () {
    const [queryParams, setQueryParams]: any = useSearchParams();

    const [masterRoles, setMasterRoles] = useState([]);
    const [selectDialogOpen, setSelectDialogOpen] = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [selectedData, setSelectedData]: [Data | null, any] = useState(null);
    const [loadingRoles, setLoadingRoles] = useState(true);
    const [formKey, setFormKey] = useState(0);
    const navigate = useNavigate();
    const apiService = useApiService();
    const canWriteConnections = useAttributePermission("APP_MANAGEMENT", "CONNECTIONS", "ManageConnections");
    const canDeleteConnections = canWriteConnections;
    const tableRef = useRef<ArtTableHandle>(null);

    interface Data {
      id: string;
      username: string;
      profile: {
        first_name: string;
        last_name: string;
      };
      // roles: string[];
      roles: { id: string; title: string }[];
    }

    const headCells: HeadCell<Data>[] = [
      {
        id: "first_name" as keyof Data,
        numeric: false,
        disablePadding: false,
        label: "First name",
        getColData: (row) => (
          <div
            data-qa="redirect"
            onClick={() =>
              navigate(`/application-settings/connections/profile-insight/${row.id}`)
            }
          >
            {row?.profile?.first_name}
          </div>
        ),
      },
      {
        id: "last_name" as keyof Data,
        numeric: false,
        disablePadding: false,
        label: "Last name",
        getColData: (row) => (
          <div
            data-qa="redirect"
            onClick={() =>
              navigate(`/application-settings/connections/profile-insight/${row.id}`)
            }
          >
            {row.profile?.last_name}
          </div>
        ),
      },
      {
        id: "username",
        numeric: false,
        disablePadding: false,
        label: "Username",
        getColData: (row) => (
          <div
            data-qa="redirect"
            onClick={() =>
              navigate(`/application-settings/connections/profile-insight/${row.id}`)
            }
          >
            {row.username}
          </div>
        ),
      },
      {
        id: "roles",
        numeric: false,
        disablePadding: false,
        label: "Roles",
        getColData: (row) => {
          const roles = row?.roles ?? [];
          const hiddenRoles = roles.slice(3);

          return (
            <div className="d-flex gap-2 flex-wrap"
              data-qa="redirect"
              onClick={() => navigate(`/application-settings/connections/profile-insight/${row.id}`)}
              style={{ minHeight: '20px' }}
            >
              {roles.slice(0, 3).map((role: any) => (
                <Chip key={role.id} size="small" label={role.title}
                  sx={{
                    '& .MuiChip-label': {
                      color: 'white',
                      padding: '16px',
                      fontSize: '14px'
                    }
                  }}
                />
              ))}
              {roles.length > 3 && (
                <Tooltip
                  title={hiddenRoles.map(role => role.title).join(', ')}
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: '#04adc3',
                        '& .MuiTooltip-arrow': {
                          color: '#04adc3'
                        }
                      }
                    }
                  }}
                >
                  <Chip size="small" label={`+${roles.length - 3}`}
                    sx={{
                      '& .MuiChip-label': {
                        color: 'white',
                        padding: '16px',
                        fontSize: '14px'
                      }
                    }}
                  />
                </Tooltip>
              )}
            </div>
          );
        }
      },
    ];

    const tableBuilder = SSRTableBuilder<Data>({
      endpoint: "/art-users",
      method: "get",
      apiService: apiService,

      onFetch: (params) => {
        let currentPage = params["page"] || "1";
        let searchText = params["search_text"] || ""; // search
        let rolesSearch = params["roles"];

        //page and search added to params
        const newParams: any = {
          page: currentPage,
        };
        if (searchText) {
          newParams.search = searchText;
        }
        if (rolesSearch) {
          newParams.roles = rolesSearch;
        }

        setQueryParams(newParams, tableParamNav(queryParams, "page", currentPage));

        return {
          page: currentPage,
          search_text: searchText,
          roles: rolesSearch,
        };
        // return {
        //     "page": params["page"],
        //     "search_text": params["search_text"],
        //     "roles": params["roles"],
        // };
      },
      onResponse: (data) => {
        return {
          data: data.data.users ?? [],
          total: data.data.total_count,
        };
      },
    });

    useEffect(() => {
      apiService
        .get("/roles/list")
        .then(({ data: { data } }) => {
          setMasterRoles(data.roles || []);
        })
        .finally(() => {
          setLoadingRoles(false);
        });
    }, []);

    const openAssignRoleDialog = (data?: Data) => {
      setSelectedData(data);
      setSelectDialogOpen(true);
    };
    const closeAssignRoleDialog = () => {
      setSelectedData(null);
      setSelectDialogOpen(false);
      tableRef.current?.refresh();
    };

    const filterBuilder = useCallback(() => {
      return [
        {
          id: "roles",
          type: "select",
          label: "Role",
          marginBottom: 14,
          // getOptions: async () => (masterRoles ? masterRoles : []),
          getOptions: async () => {
            if (!masterRoles || masterRoles.length === 0) {
              return [{ id: '', title: 'No roles' }];
            }
            return masterRoles;
          },

          getOptionLabel: (data: any) => data["title"],
          getOptionValue: (data: any) => data["id"],
        },
        {
          type: "row",
          classNames: [""],
          children: [
            {
              type: "col",
              classNames: ["col-auto"],
              children: [
                {
                  type: "submit-button",
                  label: "Filter",
                },
              ],
            },
            {
              type: "col",
              classNames: ["col-auto"],
              children: [
                {
                  type: "reset-button",
                  label: "Reset",
                  onClick: () => {
                    setFormKey((prev) => prev + 1);
                  },
                },
              ],
            },
          ],
        },
      ] as FormFieldConsolidatedProps[];
    }, [masterRoles]);

    const openAddUserDialog = (data?: Data) => {
      setSelectedData(data);
      setAddDialogOpen(true);
    };

    const closeAddUserDialog = () => {
      setSelectedData(null);
      setAddDialogOpen(false);
    };

    const onSave = () => {
      closeAddUserDialog();
      tableRef.current?.refresh(0);
    };

    if (loadingRoles) {
      return <LinearProgress sx={{ margin: "-20px" }} />;
    }

    return (
      <>
        <ArtDialog
          title={selectedData ? "Edit User" : "Add User"}
          open={addDialogOpen}
          onClose={closeAddUserDialog}
          type="form"
          actionBuilder={(props) => {
            return (
              <Box sx={{ display: "flex", gap: "15px" }}>
                <ArtButton
                  buttonType="button"
                  dataQa="btn-save"
                  label={selectedData ? "Update" : "Add"}
                  onClick={() => props.submit?.()}
                />
                <ArtButton
                  buttonType="button"
                  dataQa="btn-reset"
                  label="Reset"
                  variant="outlined"
                  color="error"
                  onClick={() => props.reset?.()}
                />
              </Box>
            );
          }}
        >
          <ArtUserAddPage data={selectedData} onSave={onSave} />
        </ArtDialog>
        <ArtDialog
          size="large"
          title="Assign Roles"
          open={selectDialogOpen}
          onClose={closeAssignRoleDialog}
        >
          <ArtRoleAssignDialog
            data={selectedData}
            masterRoles={masterRoles}
            closeDialog={closeAssignRoleDialog}
          ></ArtRoleAssignDialog>
        </ArtDialog>

        <Box >
          <ArtTable
            key={formKey}
            ref={tableRef}
            headCells={headCells}
            builder={tableBuilder}
            // search={{ enabled: true, value: "" }}
            search={{
              enabled: true,
              value: queryParams.get("search") || "", // check and add the search text from params
            }}
            pagination={{
              initialPage: parseInt(queryParams.get("page") || "1") - 1,
            }}
            filter={{
              enabled: true,
              builder: filterBuilder,
            }}
            toolbarActions={[
              <AttributePermissionGate application="APP_MANAGEMENT" feature="CONNECTIONS" attribute="ManageConnections">
                <ArtButton
                  variant="contained"
                  label="Add"
                  dataQa="btn-add"
                  icon={faPlus}
                  onClick={(_data) => {
                    openAddUserDialog();
                  }}
                />
              </AttributePermissionGate>,
            ]}
            rowActions={[
              ...(canWriteConnections ? [
                {
                  icon: faEdit,
                  label: "Edit",
                  onClick: async (row: Data) => {
                    openAddUserDialog(row);
                  },
                },
                {
                  icon: faPlus,
                  label: "Assign Roles",
                  onClick: async (row: Data) => {
                    openAssignRoleDialog(row);
                  },
                },
              ] : []),
              ...(canDeleteConnections ? [
                {
                  icon: faTrash,
                  label: "Delete",
                  onClick: (row: Data) => {
                    confirmDialog("confirm", "Do you want to delete?").then(() => {
                      apiService.deleteData(`/art-users/${row.id}`, {
                        onSuccess: () => {
                          onSave();
                        },
                        onError: (err: any) => {
                          console.log(err);
                        },
                      })();
                    });
                  },
                },
              ] : []),
            ]}
          />
        </Box>


      </>
    );
  },
  {
    page_title: "Live Connections",
  }
);

export { ArtConnectionList };
